import { 
  Directive, 
  Input, 
  ElementRef, 
  Renderer2, 
  OnInit,
  OnDestroy 
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { UserContextService } from '../../services/common/user-context.service';
import { AccessType } from '../../models/authorization';

/**
 * Attribute directive to disable elements if user lacks required access
 * 
 * Usage examples:
 * - Disable button: <button [disableIfNoAccess]="'Administration.Users.Page':'Write'">Save</button>
 * - Disable input: <input [disableIfNoAccess]="'Configuration.Settings.Feature':'Write'" />
 * - Disable select: <mat-select [disableIfNoAccess]="'Projects.Assignments.Feature':'Write'"></mat-select>
 */
@Directive({
  selector: '[disableIfNoAccess]'
})
export class DisableIfNoAccessDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private permissionKey: string = '';
  private requiredAccess: AccessType = AccessType.Read;

  @Input() 
  set disableIfNoAccess(value: string) {
    this.parseAccessExpression(value);
    this.updateDisabledState();
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private userContextService: UserContextService
  ) {}

  ngOnInit() {
    // React to context changes
    this.userContextService.userContext$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDisabledState();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Parse access expression in format "permissionKey:access"
   */
  private parseAccessExpression(value: string): void {
    const [key, access] = value.split(':');
    
    if (!key || !access) {
      console.warn('[DisableIfNoAccessDirective] Invalid format. Expected "key:access"', value);
      return;
    }

    this.permissionKey = key.trim();
    this.requiredAccess = access.trim() as AccessType;
  }

  /**
   * Update disabled state based on user access
   */
  private updateDisabledState(): void {
    const hasAccess = this.userContextService.hasAccess(
      this.permissionKey, 
      this.requiredAccess
    );

    if (!hasAccess) {
      this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
      this.renderer.addClass(this.el.nativeElement, 'disabled');
      
      // For Angular Material components
      if (this.el.nativeElement.classList.contains('mat-mdc-button') ||
          this.el.nativeElement.classList.contains('mat-mdc-raised-button')) {
        this.renderer.addClass(this.el.nativeElement, 'mat-button-disabled');
      }
    } else {
      this.renderer.removeAttribute(this.el.nativeElement, 'disabled');
      this.renderer.removeClass(this.el.nativeElement, 'disabled');
      this.renderer.removeClass(this.el.nativeElement, 'mat-button-disabled');
    }
  }
}
